import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { Post } from "./getPost";

// Define input schema for pagination
const getPostsInputSchema = z.object({
  page: z.number().optional().default(1),
  limit: z.number().optional().default(10),
});

export type GetPostsResult = {
  posts: Post[];
  totalPosts: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

// Server function to fetch posts with pagination
export const getPosts = createServerFn({ method: "GET" })
  .validator(getPostsInputSchema)
  .handler(async (ctx) => {
    // Dynamic imports for server-side code
    const fs = await import("node:fs/promises");
    const path = await import("node:path");
    const matter = (await import("gray-matter")).default;

    const { page, limit } = ctx.data;

    const postsDirectory = path.join(process.cwd(), "content/blog");
    // Get file names under /posts
    const fileNames = await fs.readdir(postsDirectory);
    const allPostsDataPromises = fileNames
      .filter((fileName) => fileName.endsWith(".md")) // Ensure we only process markdown files
      .map(async (fileName) => {
        // Read markdown file as string
        const fullPath = path.join(postsDirectory, fileName);
        const fileContents = await fs.readFile(fullPath, "utf8");

        // Use gray-matter to parse the post metadata section
        const matterResult = matter(fileContents);
        const slug = matterResult.data.slug || fileName.replace(/\.md$/, "");

        // Create a post object with data from frontmatter
        return {
          slug,
          ...matterResult.data,
        } as Post;
      });

    const allPostsData = await Promise.all(allPostsDataPromises);

    // Sort posts by date (newest first)
    const sortedPosts = allPostsData.sort((a, b) => {
      return b.date.getTime() - a.date.getTime();
    });

    // Calculate pagination
    const totalPosts = sortedPosts.length;
    const totalPages = Math.ceil(totalPosts / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;

    // Get posts for current page
    const paginatedPosts = sortedPosts.slice(startIndex, endIndex);

    return {
      posts: paginatedPosts,
      totalPosts,
      totalPages,
      currentPage: page,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    } as GetPostsResult;
  });
